import {MustacheService} from "../../../mhg-catalog/node_modules/dc-services/mustache.service";
import {dealerViewModel, productTags, matchesFilter} from "./dealerViewModel";

const TEMPLATE_PATH = "/layout/frontend/mhg-global/templates/dealersearch";

async function buildCard(dealer, lang, config, onHover, onClick) {
    const base = dealerViewModel(dealer, config);
    const distance = parseFloat(dealer.distance);
    const tags = productTags(dealer, lang);

    const rendered = await MustacheService.getRenderedTemplate(TEMPLATE_PATH, 'dealerSearchCard.mustache', {
        ...base,
        id: dealer.id,
        hasDistance: !isNaN(distance) && distance > 0,
        distance: Math.round(distance),
        hasRoles: base.hasRoleLabel || tags.length > 0,
        hasTags: tags.length > 0,
        tags: tags.map(tag => ({tag})),
        linkLabel: config.linkLabel[base.lvl] || config.linkLabel[0]
    });

    const card = rendered[0];
    card.addEventListener("mouseenter", () => onHover(dealer.id, true));
    card.addEventListener("mouseleave", () => onHover(dealer.id, false));
    card.addEventListener("click", () => onClick(dealer.id));
    return card;
}

async function buildPopup(dealer, config) {
    const rendered = await MustacheService.getRenderedTemplate(TEMPLATE_PATH, 'dealerPopup.mustache', dealerViewModel(dealer, config));
    return rendered[0];
}

// Builds the dealer list (cards), the map markers, and keeps hover/selection state
// in sync between them. Returned `render`/`setFilter` are the only things the
// orchestrator needs to know about.
export function createDealerList({listContainer, emptyNotice, map, markerCluster, markerIconDefault, markerIconActive, lang, config}) {
    let dealers = [];
    let entries = new Map(); // dealerId -> {dealer, marker, card, iconActive}
    let activeFilter = "all";
    let selectedId = null;

    function isSelected(id) {
        return String(id) === String(selectedId);
    }

    function setMarkerActive(entry, active) {
        if (entry.iconActive === active) return;
        entry.iconActive = active;
        entry.marker.setIcon(active ? markerIconActive : markerIconDefault);
    }

    // Temporary hover highlight — never overrides the clicked selection.
    function setHover(id, on) {
        if (isSelected(id)) return;
        const entry = entries.get(String(id));
        if (!entry) return;
        entry.card.classList.toggle("is-active", on);
        setMarkerActive(entry, on);
    }

    // Persistent selection — stays active (red) until another dealer is clicked.
    function selectDealer(id) {
        selectedId = id;
        entries.forEach(entry => {
            const active = String(entry.dealer.id) === String(id);
            entry.card.classList.toggle("is-active", active);
            setMarkerActive(entry, active);
        });
    }

    function focusDealer(id) {
        const entry = entries.get(String(id));
        if (!entry) return;
        map.flyTo(entry.marker.getLatLng(), Math.max(map.getZoom(), 12), {duration: 0.6});
        markerCluster.zoomToShowLayer(entry.marker, () => entry.marker.openPopup());
    }

    function scrollToDealer(id) {
        const entry = entries.get(String(id));
        if (!entry) return;
        entry.card.scrollIntoView({behavior: "smooth", block: "center"});
    }

    async function buildEntry(dealer) {
        const latitude = parseFloat(dealer.lat);
        const longitude = parseFloat(dealer.lng);
        if (isNaN(latitude) || isNaN(longitude)) return null;

        const [popup, card] = await Promise.all([
            buildPopup(dealer, config),
            buildCard(dealer, lang, config, setHover, (id) => {
                selectDealer(id);
                focusDealer(id);
            })
        ]);

        const marker = L.marker([latitude, longitude], {icon: markerIconDefault})
            .bindPopup(popup, {minWidth: 390, maxWidth: 390});
        marker.on("mouseover", () => setHover(dealer.id, true));
        marker.on("mouseout", () => setHover(dealer.id, false));
        marker.on("click", () => {
            selectDealer(dealer.id);
            scrollToDealer(dealer.id);
            marker.openPopup();
        });

        return {dealer, marker, card};
    }

    async function render() {
        markerCluster.clearLayers();
        listContainer.innerHTML = "";
        entries = new Map();
        selectedId = null;

        const visible = dealers.filter(dealer => matchesFilter(dealer, activeFilter));
        const built = (await Promise.all(visible.map(buildEntry))).filter(Boolean);

        built.forEach(({dealer, marker, card}) => {
            markerCluster.addLayer(marker);
            listContainer.appendChild(card);
            entries.set(String(dealer.id), {dealer, marker, card, iconActive: false});
        });

        if (emptyNotice) emptyNotice.hidden = entries.size > 0;

        if (entries.size > 0) {
            map.fitBounds(markerCluster.getBounds(), {padding: [40, 40]});
        }
    }

    function setDealers(newDealers) {
        dealers = newDealers;
    }

    function setFilter(filter) {
        activeFilter = filter;
    }

    return {render, setDealers, setFilter};
}
